<template>
  <div>
    <vs-alert active="false">Welcome to Reporting Dashboard of Smart Invoice Platform.</vs-alert>
    <div class="invisible">Empty container</div>

    <!-- Date Range Picker section -->
    <vx-card class="mt-6">
      <div class="card-header-heading">SELECT DATE RANGE</div>
      <div class="invisible">Empty container</div>
      <div class="date-picker">
        <label for="fromDate">From:</label>
        <input type="datetime-local" v-model="fromDate" />
        <label for="toDate">To:</label>
        <input type="datetime-local" v-model="toDate" />
        <button @click="fetchData" class="btn btn-primary">Apply</button>
      </div>
      <div class="date-buttons">
        <button @click="setDefaultDatesBtn" class="btn btn-primary">This Month</button>
        <button @click="setToday" class="btn btn-secondary">Today</button>
        <button @click="fetchAllDates" class="btn btn-secondary">Fetch All Dates</button>
      </div>
    </vx-card>

    <!-- Reconciliation Report Table -->
    <div class="row mb-4">
      <div class="col-md-12">
        <div class="card">
          <div class="card-body">
            <button @click="exportReconciliationReportToCSV" class="btn btn-secondary mb-2">Export CSV</button>
            <div class="card-header-heading">RECONCILIATION REPORT</div>
            <div class="table-responsive report-table-responsive">
              <table class="report-table">
                <thead>
                  <tr>
                    <th>eDocument Type</th>
                    <th>Inbound Count</th>
                    <th v-for="status in statusTypes" :key="status">{{ formatStatus(status) }}</th>
                    <th>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in reportData" :key="item.type">
                    <td>{{ item.type }}</td>
                    <td>{{ item.inboundCount }}</td>
                    <td v-for="status in statusTypes" :key="status">{{ item[status] }}</td>
                    <td>{{ item.totalCount }}</td>
                  </tr>
                  <tr class="total-row">
                    <td>Total</td>
                    <td>{{ totalCounts.inboundCount }}</td>
                    <td v-for="status in statusTypes" :key="status">{{ totalCounts[status] }}</td>
                    <td>{{ totalCounts.totalCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  created() {
    this.setDefaultDates();
    this.fetchData();
  },
  methods: {
    setDefaultDates() {
      const now = new Date();
      this.fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, -1);
      this.toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString().slice(0, -1);
    },
    setDefaultDatesBtn() {
      const now = new Date();
      this.fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, -1);
      this.toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString().slice(0, -1);
      this.fetchData();
    },
    setToday() {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const formatDateTime = (date) => {
        const pad = (num) => (num < 10 ? '0' : '') + num;
        return date.getFullYear() + '-' +
          pad(date.getMonth() + 1) + '-' +
          pad(date.getDate()) + 'T' +
          pad(date.getHours()) + ':' +
          pad(date.getMinutes()) + ':' +
          pad(date.getSeconds());
      };
      this.fromDate = formatDateTime(startOfDay);
      this.toDate = formatDateTime(now);
      this.fetchData();
    },
    fetchAllDates() {
      const now = new Date();
      const twentyYearsAgo = new Date(now.getFullYear() - 20, now.getMonth(), now.getDate());
      this.fromDate = twentyYearsAgo.toISOString().slice(0, -1);
      this.toDate = now.toISOString().slice(0, -1);
      this.fetchData();
    },
    async fetchData() {
      try {
        const response = await this.$store.dispatch('einvoice/fetchEInvoiceCount', {
          from: this.fromDate,
          to: this.toDate
        });
        console.log('Dashboard Einvoice count Data:', response);
        this.processEinvoiceCount(response.data);

        const statusResponse = await this.getEinvoiceStatusRecords();
        this.processEinvoiceData(statusResponse);
      } catch (e) {
        console.error('Error fetching Einvoice count data:', e);
      }
    },
    async getEinvoiceStatusRecords() {
      try {
        const response = await this.$store.dispatch('einvoice/fetchEInvoiceRecord', {
          from: this.fromDate,
          to: this.toDate,
          s: this.search,
          join: 'billingDoc'
        });
        return response.data;
      } catch (e) {
        console.error(e.toString());
        return [];
      }
    },
    processEinvoiceCount(data) {
      const countByStatus = data.countByStatus;
      const reportData = [];
      const statusTypes = Object.keys(countByStatus);
      const totalCounts = { inboundCount: 0, totalCount: 0 };
      statusTypes.forEach(status => {
        totalCounts[status] = 0;
      });

      const eDocumentTypes = Object.keys(countByStatus['pre creation']).map(key => countByStatus['pre creation'][key].type);

      eDocumentTypes.forEach(type => {
        if (type !== 'pro-forma') {
          const item = { type, inboundCount: 0, totalCount: 0 };

          statusTypes.forEach(status => {
            const statusData = countByStatus[status].find(entry => entry.type === type);
            const count = statusData ? parseInt(statusData.count, 10) : 0; // Ensure count is an integer
            item[status] = count;
            totalCounts[status] += count;
          });

          item.inboundCount = statusTypes.reduce((sum, status) => sum + item[status], 0);
          item.totalCount = item.inboundCount;
          totalCounts.inboundCount += item.inboundCount;
          totalCounts.totalCount += item.totalCount;
          reportData.push(item);
        }
      });

      this.reportData = reportData;
      this.totalCounts = totalCounts;
      this.statusTypes = statusTypes;

      this.transactionCounts = {};
      statusTypes.forEach(status => {
        const totalTransactionCount = countByStatus[status].reduce((sum, item) => sum + parseInt(item.count, 10), 0); // Ensure count is summed as integer
        this.transactionCounts[status] = totalTransactionCount;
      });
    },
    processEinvoiceData(data) {
      const groupedData = data.reduce((acc, item) => {
        const status = item.status.toLowerCase();
        if (!acc[status]) acc[status] = [];
        acc[status].push({
          createdAt: item.createdAt,
          docNo: item.docNo,
          lhdnEInvoiceUuid: item.lhdnEInvoiceUuid,
          validationDate: item.validationDate,
          buyer: item.billingDoc.buyer,
          supplier: item.billingDoc.supplier,
          total: item.billingDoc.total,
          type: this.mapDocType(item.type),
          invoiceDate: item.invoiceDate
        });
        return acc;
      }, {});

      this.einvoiceDataByStatus = groupedData;
    },
    mapDocType(type) {
      const typeMapping = {
        '01': 'INVOICE',
        '02': 'CREDIT NOTE',
        '03': 'DEBIT NOTE',
        '04': 'REFUND NOTE',
        '11': 'SELF-BILLED INVOICE',
        '12': 'SELF-BILLED CREDIT NOTE',
        '13': 'SELF-BILLED DEBIT NOTE',
        '14': 'SELF-BILLED REFUND NOTE'
      };
      return typeMapping[type] || type;
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : '';
    },
    formatStatus(status) {
      return status.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    },
    exportReconciliationReportToCSV() {
      const csvData = [];
      const dateCreated = new Date().toLocaleDateString();
      const dateRange = `From ${this.formatDate(this.fromDate)} To ${this.formatDate(this.toDate)}`;
      csvData.push(`Report Created: ${dateCreated}`);
      csvData.push(`Date Range: ${dateRange}`);
      csvData.push('');

      const headers = ['eDocument Type', 'Inbound Count', ...this.statusTypes.map(this.formatStatus), 'Total Count'];
      csvData.push(headers.join(','));

      this.reportData.forEach(item => {
        const row = [
          item.type,
          item.inboundCount,
          ...this.statusTypes.map(status => item[status] || 0),
          item.totalCount
        ];
        csvData.push(row.join(','));
      });

      const totalRow = [
        'Total',
        this.totalCounts.inboundCount,
        ...this.statusTypes.map(status => this.totalCounts[status] || 0),
        this.totalCounts.totalCount
      ];
      csvData.push(totalRow.join(','));

      const csvString = csvData.join('\n');
      this.downloadCSV(csvString, 'Reconciliation_Report');
    },
    exportEDocumentByStatusToCSV() {
      const csvData = [];
      const dateCreated = new Date().toLocaleDateString();
      const dateRange = `From ${this.formatDate(this.fromDate)} To ${this.formatDate(this.toDate)}`;
      csvData.push(`Report Created: ${dateCreated}`);
      csvData.push(`Date Range: ${dateRange}`);
      csvData.push('');

      const headers = [
        'eDocument Date', 'e-Document', 'Unique Identifier Number (UIN)', 'Validation Date',
        "Buyer's Name", "Seller's Name", 'Amount', 'e-Document Type', 'Submission Date'
      ];
      csvData.push(headers.join(','));

      const statusesToExport = this.selectedStatus === '' ? this.statusTypes : [this.selectedStatus];
      statusesToExport.forEach(status => {
        csvData.push('');
        csvData.push(`Status: ${this.formatStatus(status)}`);
        if (this.einvoiceDataByStatus[status] && this.einvoiceDataByStatus[status].length > 0) {
          this.einvoiceDataByStatus[status].forEach(doc => {
            const row = [
              this.formatDate(doc.createdAt),
              doc.docNo,
              doc.lhdnEInvoiceUuid,
              this.formatDate(doc.validationDate),
              doc.buyer.name,
              doc.supplier.name,
              doc.total,
              doc.type,
              this.formatDate(doc.invoiceDate)
            ];
            csvData.push(row.join(','));
          });
        }
      });

      const csvString = csvData.join('\n');
      this.downloadCSV(csvString, 'eDocument_By_Status_Report');
    },
    downloadCSV(csvString, filename) {
      const blob = new Blob([csvString], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    }
  },
  data() {
    return {
      reportData: [],
      totalCounts: {},
      fromDate: '',
      toDate: '',
      statusTypes: [],
      einvoiceDataByStatus: {},
      transactionCounts: {},
      reconciliationReportVisible: true,
      eDocumentByStatusVisible: true,
      selectedStatus: '' // New data property to store the selected status
    };
  },
  computed: {
    filteredEinvoiceData() {
      const filteredData = { ...this.einvoiceDataByStatus };
      delete filteredData['pre creation'];

      if (this.selectedStatus === '') {
        return filteredData;
      }
      return { [this.selectedStatus]: filteredData[this.selectedStatus] };
    },
    filteredStatusTypes() {
      return this.statusTypes.filter(status => status !== 'pre creation');
    }
  }
};
</script>

<style>
/* Date Range Picker Styles */
.date-range-card {
  background-color: #fafcfd;
}

.date-picker {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.date-picker label {
  margin: 0 10px;
}

.date-picker input {
  flex: 1;
  padding: 5px;
  margin: 0 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.date-buttons {
  display: flex;
  justify-content: space-around;
}

.btn {
  padding: 10px 20px;
  margin: 5px;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
}

.btn-primary {
  background-color: #84a3b9;
}

.btn-secondary {
  background-color: #84a3b9;
}

/* Table Styles */
.report-table-responsive {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 800px;
  /* Adjust as necessary */
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
}

.report-table th,
.report-table td {
  border: 2px solid #000;
  padding: 8px;
}

.report-table th {
  background-color: #d3d3d3;
  text-align: left;
}

.report-table .total-row {
  background-color: #d3d3d3;
  font-weight: bold;
}

/* Card Header Styles */
.card-header-heading {
  text-align: left;
  background-color: #84a3b9;
  padding: 10px;
  border-bottom: 1px solid #ccc;
  margin-bottom: 10px;
}

.transaction-count {
  margin-bottom: 10px;
}

/* Invisible Container Styles */
.invisible {
  visibility: hidden;
}
</style>
